import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  openOnlinesByPainter,
  openTemplatesByPublisher,
} from "commons/worksLinks";
import {
  DotsHorizontalIcon,
  Pencil1Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import useSWRMutation from "swr/mutation";
import { delFetcher } from "api/swr";
import { Badge } from "@/components/ui/badge";
import { Dot } from "components/Dot";
import { getTemplateStateInfo } from "../types/templateState";
import { isChildSpineTemplate } from "../types/spine-template";
import { useForm } from "./TemplatesTab";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { AvatarMediaPreview } from "AdminPanel/SpineAvatars/SpineAvatars/components/AvatarMediaPreview";

function formatSpineTemplateScheduledTime(timestamp: number) {
  const date = new Date(timestamp);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const DataTableRowDelete = ({ row }: { row: any }) => {
  const mutate = useForm((s) => s.mutate);
  const data = row.original;
  const { toast } = useToast();
  const { trigger, isMutating } = useSWRMutation(
    "/spine-templates",
    delFetcher,
    {
      onSuccess: (res) => {
        if (res.code === 403) {
          toast({
            title: "Error",
            description: res.message,
            variant: "destructive",
          });
          return;
        }
        if (mutate) mutate(); // 刷新列表
        toast({
          title: "Template Deleted",
          description: "The Template has been successfully deleted.",
          className: "bg-green-500 text-white",
        });
      },
      onError: (error: Error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    },
  );
  const [open, setOpen] = useState(false);
  const handleDelete = async () => {
    await trigger({ id: data.id });
    setOpen(false);
  };
  const openDialog = (e?: Event) => {
    e?.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <DropdownMenuItem
        className="text-destructive focus:bg-destructive focus:text-destructive-foreground"
        onSelect={openDialog}
      >
        <TrashIcon className="mr-2 h-4 w-4" />
        Delete{isMutating && "ing..."}
      </DropdownMenuItem>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Are you sure you want to delete this template?
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isMutating}
            >
              {isMutating ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DataTableRowActions = ({ row }: { row: any }) => {
  return (
    <div className="flex gap-2 items-center">
      <Button asChild>
        <Link to={`/admin/spine/editor/${row.original.id}`}>
          <Pencil1Icon className="h-4 w-4 mr-2" /> Paint now
        </Link>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex h-8 w-8 p-0 data-[state=open]:bg-muted"
          >
            <DotsHorizontalIcon className="h-4 w-4" />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[160px]">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={`/admin/spine-avatars?tid=${encodeURIComponent(row.original.id)}`}>
              Avatars
            </Link>
          </DropdownMenuItem>
          <DataTableRowDelete row={row} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export const columns = [
  {
    header: "#",
    cell: ({ row }: { row: { index: number } }) => row.index + 1,
  },
  {
    header: "Cover",
    accessorKey: "cover",
    cell: ({ row }: { row: any }) => {
      const data = row.original;
      const videoCover =
        data.videoCoverPreview || data.videoCover || data.coverVideo;
      return data.cover || videoCover ? (
        <div className="aspect-square rounded-md h-24 w-24 overflow-hidden border border-border bg-black">
          <AvatarMediaPreview
            videoUrl={videoCover}
            imageUrl={data.cover}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div className="aspect-square rounded-md bg-muted h-24 w-24 flex items-center justify-center text-[10px] text-muted-foreground border border-border leading-tight text-center px-1">
          无 Cover
        </div>
      );
    },
  },
  {
    header: "Preview",
    accessorKey: "preview",
    cell: ({ row }: { row: any }) => {
      const preview = row.original.preview;
      const videoPreview =
        row.original.videoPreviewPreview ||
        row.original.videoPreview ||
        row.original.previewCover ||
        row.original.videoCoverPreview ||
        row.original.videoCover;
      return preview || videoPreview ? (
        <div className="aspect-square rounded-md h-24 w-24 overflow-hidden border border-border bg-black">
          <AvatarMediaPreview
            videoUrl={videoPreview}
            imageUrl={preview}
            className="h-full w-full"
          />
        </div>
      ) : (
        <div className="aspect-square rounded-md bg-muted h-24 w-24 flex items-center justify-center text-xs text-muted-foreground border border-border">
          无 Preview
        </div>
      );
    },
  },
  {
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="fw-bold">{row.original.name}</div>
          {isChildSpineTemplate(row.original) && (
            <Badge variant="outline" className="text-[10px] h-5 px-1.5">
              子模版
            </Badge>
          )}
        </div>
        <div className="text-black/90 text-xs">{row.original.id}</div>
        {isChildSpineTemplate(row.original) && row.original.parentId && (
          <div className="text-[11px] text-muted-foreground">
            父模版: {row.original.parentId}
          </div>
        )}
        <p className="text-ellipsis w-56 text-black/60 text-xs line-clamp-2">
          {row.original.intro}
        </p>
      </>
    ),
  },
  {
    header: "Type",
    accessorKey: "type",
    cell: ({ row }: { row: any }) => {
      const type = row.original.type || "-";
      return <span className="text-sm">{type}</span>;
    },
  },
  {
    header: "State",
    accessorKey: "state",
    cell: ({ row }: { row: any }) => {
      const s = row.original.state;
      const stateInfo = getTemplateStateInfo(s);
      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            {stateInfo ? (
              <>
                <Dot className={stateInfo.className} />
                <span>{stateInfo.label}</span>
              </>
            ) : s != null ? (
              <span className="text-muted-foreground">{s}</span>
            ) : (
              <span className="text-muted-foreground">-</span>
            )}
          </div>
          {s === 100 && row.original.scheduledOnlineTime ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {formatSpineTemplateScheduledTime(
                row.original.scheduledOnlineTime,
              )}
            </div>
          ) : null}
        </div>
      );
    },
  },
  {
    header: "Publisher",
    accessorKey: "publisher",
    cell: ({ row }: { row: any }) => {
      const publisherId = row.original.painter;
      const name = row.original.publisher || publisherId;
      if (!publisherId) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <button
          type="button"
          className="font-medium underline underline-offset-4"
          title={publisherId}
          onClick={() => openTemplatesByPublisher(publisherId)}
        >
          {name}
        </button>
      );
    },
  },
  {
    header: "Painter",
    accessorKey: "painter",
    cell: ({ row }: { row: any }) => {
      const painterId = row.original.author;
      const name = row.original.onlinePublisher || painterId;
      if (!painterId) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <button
          type="button"
          className="font-medium underline underline-offset-4"
          title={painterId}
          onClick={() => openOnlinesByPainter(painterId)}
        >
          {name}
        </button>
      );
    },
  },
  {
    header: "Publish Time",
    accessorKey: "publishTime",
    cell: ({ row }: { row: any }) => (
      <DisplayUpdateTime updateTime={row.original.publishTime} />
    ),
  },
  {
    header: "Actions",
    cell: ({ row }: { row: any }) => <DataTableRowActions row={row} />,
  },
];
